import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PublishedTitle {
  title: string;
  /** The pathname the title was published for — see `PageTitleProvider`. */
  pathname: string;
}

interface PageTitleContextType {
  /** The page-published title, or null when the page published nothing. */
  published: PublishedTitle | null;
  setPageTitle: (title: string | null) => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

/**
 * Default value is a no-op rather than `undefined`, so `usePageTitle` is safe to
 * call outside the provider. `PageHeader` publishes through it and is rendered
 * by ~16 pages, many of which are unit-tested in isolation without the layout
 * shell — those tests must not have to mount a provider.
 */
const PageTitleContext = createContext<PageTitleContextType>({
  published: null,
  setPageTitle: () => {},
});

/**
 * Lets a page publish the title shown in the topbar (see RootLayout's
 * `PageTitle`). Mirrors `BreadcrumbContext`: pages keep calling `PageHeader`
 * with a `title`, and the layout — not the page — renders it.
 *
 * The published title is tagged with the pathname it was published for. The
 * topbar ignores a title whose pathname no longer matches the current route, so
 * navigating to a page that publishes nothing (or that publishes later, since
 * effects run after paint) never shows the previous page's stale title.
 */
export function PageTitleProvider({ children }: { children: ReactNode }): ReactNode {
  const location = useLocation();
  const [published, setPublished] = useState<PublishedTitle | null>(null);

  // Read during commit (effects run after render), so this is the pathname the
  // publishing page was rendered at.
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  const setPageTitle = useCallback((title: string | null) => {
    setPublished(title ? { title, pathname: pathnameRef.current } : null);
  }, []);

  const value = useMemo<PageTitleContextType>(
    () => ({ published, setPageTitle }),
    [published, setPageTitle],
  );

  return <PageTitleContext.Provider value={value}>{children}</PageTitleContext.Provider>;
}

export function usePageTitleContext(): PageTitleContextType {
  return useContext(PageTitleContext);
}

/**
 * Publishes `title` to the topbar for the lifetime of the calling page,
 * clearing it on unmount so the next page falls back to the nav-derived label.
 * No-op outside a `PageTitleProvider`.
 */
export function usePageTitle(title: string | null): void {
  const { setPageTitle } = usePageTitleContext();

  useEffect(() => {
    setPageTitle(title && title.length > 0 ? title : null);
    return () => setPageTitle(null);
  }, [setPageTitle, title]);
}
